import { createMainAppMicroserviceInstance } from '@servicetitan/cp-api';
import {
    ServiceAddress,
    createServiceAddress,
    TimezoneDate,
    PortalStore,
    portalStoreToken,
} from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { CustomerPortalCustomers } from './portal-customers.api';
export type { DashboardCustomerInvoicesModel, DashboardInvoiceModel } from './portal-invoices.api';
import {
    Dashboard,
    DashboardMembershipSummaryModel,
    EstimateContainerWebModel,
    JobListItemWeb,
    DashboardServiceAgreementsData,
} from './portal-dashboard.api';
import { PortalInvoices, DashboardCustomerInvoicesModel } from './portal-invoices.api';
import { PortalJobs } from './portal-jobs.api';

export interface CustomerAddresses {
    id: number;
    name: string;
    addresses: ServiceAddress[];
}

export interface VisitModel extends Omit<
    DashboardMembershipSummaryModel,
    'from' | 'to' | 'nextBillingDate' | 'customerId'
> {
    from?: TimezoneDate;
    to?: TimezoneDate;
    nextBillingDate?: TimezoneDate;
}

export interface DashboardInfo {
    customerInvoices: DashboardCustomerInvoicesModel[];
    proposals: EstimateContainerWebModel[];
    jobs?: JobListItemWeb[];
    serviceAgreements?: DashboardServiceAgreementsData[];
}

export const processVisits = (items: DashboardMembershipSummaryModel[]): VisitModel[] =>
    items.map(item => ({
        ...item,
        from: item.from ? TimezoneDate.dateFromDateLocal(item.from) : undefined,
        to: item.to ? TimezoneDate.dateFromDateLocal(item.to) : undefined,
        nextBillingDate: item.nextBillingDate
            ? TimezoneDate.dateFromDateLocal(item.nextBillingDate)
            : undefined,
    }));

@injectable()
export class DashboardApi {
    invoice: PortalInvoices;
    jobs: PortalJobs;
    private customers: CustomerPortalCustomers;
    private dashboard: Dashboard;

    constructor(@inject(portalStoreToken) private portalStore: PortalStore) {
        this.dashboard = new Dashboard(
            createMainAppMicroserviceInstance('/v1/customerportal2/dashboard', '/api/cp/dashboard')
        );
        // TODO: make it use V2 endpoint without error
        this.customers = new CustomerPortalCustomers(
            createMainAppMicroserviceInstance('/v1/customerportal2/customers', '/api/cp/customers')
        );

        this.invoice = new PortalInvoices(
            createMainAppMicroserviceInstance('/v1/customerportal2/invoices', '/api/cp/invoices')
        );

        this.jobs = new PortalJobs(
            createMainAppMicroserviceInstance('/v1/customerportal2/jobs', '/api/cp/jobs')
        );
    }

    getCustomerAddresses(): Promise<CustomerAddresses[]> {
        return this.customers.getServiceAddresses().then(({ data }) =>
            data.map(customer => ({
                id: customer.id,
                name: customer.name,
                addresses: customer.locations.map(location =>
                    createServiceAddress(location.id, location.name, location.address ?? {})
                ),
            }))
        );
    }

    getDashboardData(
        locationIds: number[],
        createdOnOrAt: Date | null | undefined
    ): Promise<DashboardInfo> {
        return this.dashboard.dashboardData(locationIds, createdOnOrAt, false).then(({ data }) => ({
            customerInvoices: data.customersInvoices,
            proposals: data.latestEstimateContainers,
            visits: processVisits(data.visitsDueMemberships ?? []),
            jobs: data.upcomingJobs,
            serviceAgreements: data.serviceAgreements ?? [],
        }));
    }
}
